// services/JobDataAdapter.ts
import { Job, PropertyDetails } from '../models/Job.interface';
import { Address } from '../models/Address.interface';
import { ChatMessageProps } from '../pages/DashboardProject/ChatMessage';

interface MySQLJobData {
  id: string;
  firebase_id?: string;
  job_title: string;
  job_description?: string;
  job_summary?: string;
  job_detail?: string;
  seo_url?: string;
  job_status: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  contact_person_role?: string;
  user_id?: string;
  property_address_line1?: string;
  property_address_line2?: string;
  property_city?: string;
  property_state?: string;
  property_zip?: string;
  property_country?: string;
  property_id?: string;
  dt_created: string;
  dt_last_modified: string;
  dt_submitted?: string;
  categories?: string[];
  attachments?: any[];
  chat_messages?: MySQLChatMessage[];
  matching_status?: string;
  matched_contractor_count?: number;
  complete?: boolean;
  contract_id?: string;
  num_bids?: number;
  
  // New job_properties fields (optional)
  propertyDetails?: PropertyDetails;
}

interface MySQLChatMessage {
  id?: string;
  message_text: string;
  sender_name: string;
  role: string;
  created_at: string;
  sender_id?: string;
}

export class JobDataAdapter {
   /**
   * Convert MySQL job data to the original Job interface that components expect
   */
  static mysqlToJob(mysqlJob: any): Job {
    /*
    console.log('🔧 JobDataAdapter.mysqlToJob called with:', mysqlJob);
    console.log('🔧 Type of input:', typeof mysqlJob);
    console.log('🔧 Object keys:', Object.keys(mysqlJob || {}));
    */
    // If mysqlJob is null/undefined, return a minimal job object
    if (!mysqlJob) {
      console.error('❌ mysqlJob is null or undefined');
      throw new Error('Cannot convert null job data');
    }

    /*
    console.log('🔧 Extracting job fields:');
    console.log('  - id:', mysqlJob.id);
    console.log('  - job_title:', mysqlJob.job_title);
    console.log('  - job_summary:', mysqlJob.job_summary);
    console.log('  - job_detail:', mysqlJob.job_detail);
    console.log('  - job_value:', mysqlJob.job_value || mysqlJob.jobValue);
    console.log('  - job_status:', mysqlJob.job_status);
    console.log('  - contact_name:', mysqlJob.contact_name);
    console.log('  - contact_email:', mysqlJob.contact_email);
    console.log('  - user_id:', mysqlJob.user_id);
    console.log('  - categories:', mysqlJob.categories);
    console.log('  - attachments:', mysqlJob.attachments);
    console.log('  - property_details:', mysqlJob.property_details);
    console.log('  - property fields:', {
      line1: mysqlJob.property_address_line1,
      line2: mysqlJob.property_address_line2,
      city: mysqlJob.property_city,
      state: mysqlJob.property_state,
      zip: mysqlJob.property_zip,
      property_id: mysqlJob.property_id
    });
    */
    // Convert address fields back to propertyAddress object
    const propertyAddress: Address | undefined = mysqlJob.property_address_line1 ? {
      address1: mysqlJob.property_address_line1,
      address2: mysqlJob.property_address_line2 || '',
      city: mysqlJob.property_city || '',
      state: mysqlJob.property_state || '',
      zipCode: mysqlJob.property_zip || '',
      country: mysqlJob.property_country || 'US',
      // CRITICAL: Include coordinates in propertyAddress
      latitude: mysqlJob.property_latitude ? parseFloat(mysqlJob.property_latitude) : undefined,
      longitude: mysqlJob.property_longitude ? parseFloat(mysqlJob.property_longitude) : undefined
    } : undefined;

    //console.log('🔧 Converted propertyAddress:', propertyAddress);

    // Convert attachments array
    const jobAttachments = (mysqlJob.attachments || []).map((att: any) => ({
      id: att.id || att.url, // Use URL as fallback ID
      url: att.url,
      caption: att.caption,
      originalFilename: att.original_filename || att.fileName,
      mimeType: att.mime_type || att.fileType,
      displayOrder: att.display_order || att.displayOrder || 0,
      order: att.display_order || att.displayOrder || 0
    }));

    //console.log('🔧 Converted attachments:', jobAttachments);

    // Convert chat messages if present
    const chatMessages = (mysqlJob.chat_messages || []).map((msg: any) => ({
      id: msg.id,
      message: msg.message_text,
      senderName: msg.sender_name,
      role: msg.role,
      timestamp: msg.created_at ? new Date(msg.created_at) : new Date()
    }));

    //console.log('🔧 Converted chat messages:', chatMessages);

    // Convert timestamps
    const dtCreated = mysqlJob.dt_created ? new Date(mysqlJob.dt_created) : new Date();
    const dtLastModified = mysqlJob.dt_last_modified ? new Date(mysqlJob.dt_last_modified) : new Date();
    const dtSubmitted = mysqlJob.dt_submitted ? new Date(mysqlJob.dt_submitted) : undefined;

    //console.log('🔧 Converted timestamps:', { dtCreated, dtLastModified, dtSubmitted });

    const convertedJob: Job = {
      id: mysqlJob.id,
      jobTitle: mysqlJob.job_title,
      jobDescription: mysqlJob.job_description,
      jobSummary: mysqlJob.job_summary,
      jobDetail: mysqlJob.job_detail,
      jobDetailModified: mysqlJob.job_detail, // Use same value
      jobValue: mysqlJob.job_value || mysqlJob.jobValue, // Support both field names
      seoUrl: mysqlJob.seo_url,
      jobStatus: mysqlJob.job_status,
      ownerName: mysqlJob.contact_name,
      ownerEmail: mysqlJob.contact_email,
      ownerPhone: mysqlJob.contact_phone,
      contactName: mysqlJob.contact_name,
      contactEmail: mysqlJob.contact_email,
      contactPhone: mysqlJob.contact_phone,
      contactPersonRole: mysqlJob.contact_person_role,
      userId: mysqlJob.user_id,
      propertyAddress,
      jobCategories: mysqlJob.categories || [],
      jobAttachments: jobAttachments, // FIXED: Use converted attachments
      dtCreated,
      dtLastModified,
      dtSubmitted,
      matchingStatus: mysqlJob.matching_status,
      matchedContractorCount: mysqlJob.matched_contractor_count,
      chatMessages,
      complete: mysqlJob.complete,
      contractId: mysqlJob.contract_id,
      numBids: mysqlJob.num_bids
    };

    // Add job_properties data if available
    if (mysqlJob.property_details) {
      convertedJob.propertyDetails = mysqlJob.property_details;
    }

    /*
    console.log('🔧 Final converted job object:', convertedJob);
    console.log('🔧 Job title after conversion:', convertedJob.jobTitle);
    console.log('🔧 Job ID after conversion:', convertedJob.id);
    console.log('🔧 Job value after conversion:', convertedJob.jobValue);
    console.log('🔧 Property Details after conversion:', convertedJob.propertyDetails);
    */
    return convertedJob;
  }
  /**
   * NEW: Convert city/state API response (array of jobs) to Job array
   */
  static mysqlJobsArrayToJobs(mysqlJobsResponse: any): Job[] {
    //console.log('🔧 JobDataAdapter.mysqlJobsArrayToJobs called with:', mysqlJobsResponse);
    
    if (!mysqlJobsResponse || !Array.isArray(mysqlJobsResponse.jobs)) {
      console.error('❌ Invalid jobs array response');
      return [];
    }

    //console.log(`🔧 Converting ${mysqlJobsResponse.jobs.length} jobs from city endpoint`);

    return mysqlJobsResponse.jobs.map((mysqlJob: any, index: number) => {
      //console.log(`🔧 Converting job ${index + 1}/${mysqlJobsResponse.jobs.length}: ${mysqlJob.job_title}`);
      //console.log(`🔧 Job ${mysqlJob.id} has ${(mysqlJob.attachments || []).length} attachments`);
      
      return this.mysqlToJob(mysqlJob);
    });
  }

  /**
   * Convert Job interface to MySQL format for API calls
   */
  static jobToMySQL(job: Job): Partial<MySQLJobData> {
    //console.log('🔧 JobDataAdapter.jobToMySQL called with:', job);
    
    const mysqlData = {
      id: job.id,
      job_title: job.jobTitle,
      job_description: job.jobDescription,
      job_summary: job.jobSummary,
      job_detail: job.jobDetailModified || job.jobDetail,
      seo_url: job.seoUrl,
      job_status: job.jobStatus,
      contact_name: job.contactName || job.ownerName || '',
      contact_email: job.contactEmail || job.ownerEmail || '',
      contact_phone: job.contactPhone || job.ownerPhone,
      contact_person_role: job.contactPersonRole,
      user_id: job.userId,
      property_address_line1: job.propertyAddress?.address1,
      property_address_line2: job.propertyAddress?.address2,
      property_city: job.propertyAddress?.city,
      property_state: job.propertyAddress?.state,
      property_zip: job.propertyAddress?.zipCode,
      property_country: job.propertyAddress?.country || 'US',
      categories: job.jobCategories,
      matching_status: job.matchingStatus,
      matched_contractor_count: job.matchedContractorCount,
      complete: job.complete,
      contract_id: job.contractId,
      num_bids: job.numBids,
      
      // Include job_properties if available
      propertyDetails: (job as any).propertyDetails
    };

    //console.log('🔧 Converted to MySQL format:', mysqlData);
    return mysqlData;
  }
}